/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const CAPABILITIES = [
  {
    icon: '💬',
    title: 'Codebase Chat',
    body: 'Ask what any file, function, or program does — get a plain English answer.'
  },
  {
    icon: '🔗',
    title: 'Dependency Analysis',
    body: 'Full import and call chains — see exactly who depends on what.'
  },
  {
    icon: '⚠️',
    title: 'Modification Risk',
    body: '"47 dependents — HIGH RISK" before you change a single line.'
  },
  {
    icon: '📚',
    title: 'Multi-level Indexing',
    body: 'Files, functions, classes, routes, procedures — all indexed.'
  },
  {
    icon: '🔬',
    title: 'Job Intelligence (iSeries)',
    body: 'STRSRVJOB runtime trace — live execution paths, service program relationships. Not static analysis.'
  },
  {
    icon: '📊',
    title: 'Job Trace UI',
    body: 'Visual execution analysis — what ran, in what order.'
  },
  {
    icon: '🗂️',
    title: 'Multi-codebase',
    body: 'Separate intelligence per project. Switch between codebases instantly.'
  }
];

const SUPPORTED = [
  'IBM iSeries/AS400 (RPGLE · CLLE · CLP · DDS · SQL)',
  'Node.js / Express.js',
  'Internal enterprise platforms'
];

const DEMO_MAILTO =
  'mailto:info@skilldelivery.co.in?subject=SkillCode%20-%20early%20access';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
      }}
    />
  </>
);

const SkillCodePage = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-10">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Software Engineering Intelligence
          </p>
          <h1 className="mt-4 text-6xl sm:text-7xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            SkillCode
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            AI built for understanding and safely evolving enterprise systems. Not a coding assistant — a software intelligence layer.
          </p>
          <div className="mt-6 flex justify-center">
            <span className="rounded-brandPill bg-brand-bgOff2 text-brand-cyan px-4 py-1 text-xs font-bold uppercase tracking-wider">
              Beta
            </span>
          </div>
        </header>

        {/* ── Hero callout ── */}
        <div className="mt-12 max-w-3xl mx-auto px-6 py-5 rounded-brandCard border-l-4 border-brand-cyan bg-brand-bgOff2">
          <p className="text-base sm:text-lg text-brand-text leading-relaxed">
            <strong className="text-brand-cyanDark">30,000+ iSeries installations</strong> in India running code nobody fully understands anymore. SkillCode does.
          </p>
        </div>

        {/* ── Capabilities grid (7 cards, 2-col) ── */}
        <section
          aria-label="Capabilities"
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {CAPABILITIES.map((c) => (
            <div
              key={c.title}
              className="bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                <span className="text-2xl leading-none" aria-hidden="true">{c.icon}</span>
              </div>
              <h3 className="font-bold text-base text-brand-text">{c.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{c.body}</p>
            </div>
          ))}
        </section>

        {/* ── Supported ── */}
        <section className="mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Supported languages and platforms
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {SUPPORTED.map((s) => (
              <span
                key={s}
                className="rounded-brandPill bg-white border border-brand-border text-brand-text px-4 py-2 text-sm font-medium"
              >
                {s}
              </span>
            ))}
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="mt-20 text-center">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Want to try SkillCode?
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
            Get in touch.
          </h2>
          <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
            SkillCode is in Beta. Contact us for early access setup and demo on your codebase.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <a
              href={DEMO_MAILTO}
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Request SkillCode access
            </a>
            <Link
              to="/domain/iseries"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              See iSeries domain
            </Link>
          </div>
        </section>

        {/* ── Inline footer ── */}
        <footer className="text-center mt-16">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default SkillCodePage;
